import React, { useContext, useState } from 'react';
import {
  TypedUseSelectorHook,
  useDispatch,
  useSelector as useReduxSelector,
} from 'react-redux';
import { useParams } from 'react-router-dom';

import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';

import { RootState } from '../../../../app/rootReducer';
import { AppDispatch } from '../../../../app/store';
import { IconLink } from '../../../../common/components/IconLink';
import { Decision } from '../../../../common/constants/decision';
import { Resource } from '../../../../common/constants/resource';
import { resolveCarrierDecisionType } from '../../../../common/constants/roles';
import ElementLocators from '../../../../common/ElementLocators';
import { AuthContext } from '../../../login/AuthWrapper';
import { postDecision } from '../../decision/decisionSlice';
import { IDecisionRequest } from '../../interfaces/IDecisionRequest';
import EditDisagreeToRescind, { Reasons } from './EditDisagreeToRescind';
import SummaryDisagreeToRescind from './SummaryDisagreeToRescind';

export const useSelector: TypedUseSelectorHook<RootState> = useReduxSelector;

export default function DisagreeToRescind() {
  const dispatch = useDispatch<AppDispatch>();
  const auth = useContext(AuthContext);
  const detailedLifecycle = useSelector(
    state => state.application.applicationDetailedLifecycle,
  );
  const decisionType = resolveCarrierDecisionType(
    auth.roles,
    detailedLifecycle,
  );
  const { id } = useParams<{ id: string }>();
  const [isOpen, setIsOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [isSummary, showSummary] = useState(false);

  if (!decisionType) {
    return null;
  }

  function onConfirmClick() {
    if (!id) {
      return;
    }
    const decision = {
      decision: Decision.APPROVED,
      decisionType,
      declineReasons: [selectedReason],
      declineDescription: description,
      carrierUnderwriterName: auth.userName,
    } as IDecisionRequest;
    dispatch(postDecision(id, decision, true));
    setIsOpen(false);
  }

  function onReviewClick() {
    if (!selectedReason || (selectedReason === Reasons.Other && !description)) {
      setShowError(true);
      return;
    }
    showSummary(true);
  }

  return (
    <>
      {isSummary ? (
        <SummaryDisagreeToRescind
          isOpen={isOpen}
          selectedReason={selectedReason}
          description={description}
          showSummary={showSummary}
          onConfirmClick={onConfirmClick}
        />
      ) : (
        <EditDisagreeToRescind
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          selectedReason={selectedReason}
          setSelectedReason={setSelectedReason}
          description={description}
          setDescription={setDescription}
          showError={showError}
          setShowError={setShowError}
          showSummary={showSummary}
          onReviewClick={onReviewClick}
        />
      )}
      <IconLink
        onClick={() => setIsOpen(true)}
        icon={faTimes}
        copy={Resource.DISAGREE_TO_RESCIND}
        locator={ElementLocators.DECLINE_DECISION_LINK}
      />
    </>
  );
}
